// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import {
  SettingsEthernetRounded,
  SignalCellular0BarRounded,
  SignalCellular1BarRounded,
  SignalCellular2BarRounded,
  SignalCellular3BarRounded,
  SignalCellular4BarRounded,
  SignalWifi1BarRounded,
  SignalWifi2BarRounded,
  SignalWifi3BarRounded,
  SignalWifi4BarRounded,
  SignalWifiOffRounded,
  UsbRounded,
} from '@mui/icons-material';

// material ui
import { Box, Divider, Popover, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import { useGatewaysGetConnectionInfoAsyncByidqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/gateways';

// shared components
import StyledBadgeWrapper from '@/components/experimental/StyledBadge';

// relative
import { GatewaySignalSparkline } from './GatewaySignalSparkline';

import { DateTime } from 'luxon';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toMs(iso) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function getLatestConnectionType(connectionStatus) {
  const candidates = [];
  const wifiTs = toMs(connectionStatus.LatestWifiTimestamp);
  if (wifiTs != null) candidates.push({ type: 'wifi', ts: wifiTs });
  const mobileTs = toMs(connectionStatus.LatestMobileTimestamp);
  if (mobileTs != null) candidates.push({ type: 'mobile', ts: mobileTs });
  const dongleTs = toMs(connectionStatus.LatestDongleTimestamp);
  if (dongleTs != null) candidates.push({ type: 'dongle', ts: dongleTs });
  const ethTs = toMs(connectionStatus.LatestEthernetTimestamp);
  if (ethTs != null) candidates.push({ type: 'ethernet', ts: ethTs });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.ts - a.ts);
  return candidates[0].type;
}

function wifiBarsFromRssi(rssi) {
  if (rssi == null) return 0;
  if (rssi >= -60) return 4;
  if (rssi >= -67) return 3;
  if (rssi >= -75) return 2;
  if (rssi >= -85) return 1;
  return 0;
}

function mobileBarsFromRssi(rssi) {
  if (rssi == null) return 0;
  if (rssi >= -75) return 4;
  if (rssi >= -85) return 3;
  if (rssi >= -95) return 2;
  if (rssi >= -105) return 1;
  return 0;
}

function WifiIcon({ rssi, offline, fontSize = 'small', colors }) {
  const props = { fontSize };
  if (offline)
    return <SignalWifiOffRounded {...props} sx={{ color: colors.offline }} />;
  const bars = wifiBarsFromRssi(rssi);
  switch (bars) {
    case 4:
      return <SignalWifi4BarRounded {...props} sx={{ color: colors.good }} />;
    case 3:
      return <SignalWifi3BarRounded {...props} sx={{ color: colors.good }} />;
    case 2:
      return <SignalWifi2BarRounded {...props} sx={{ color: colors.warn }} />;
    case 1:
      return <SignalWifi1BarRounded {...props} sx={{ color: colors.warn }} />;
    default:
      return <SignalWifiOffRounded {...props} sx={{ color: colors.bad }} />;
  }
}

function MobileIcon({ rssi, offline, fontSize = 'small', colors }) {
  const props = { fontSize };
  if (offline)
    return (
      <SignalCellular0BarRounded {...props} sx={{ color: colors.offline }} />
    );
  const bars = mobileBarsFromRssi(rssi);
  switch (bars) {
    case 4:
      return (
        <SignalCellular4BarRounded {...props} sx={{ color: colors.good }} />
      );
    case 3:
      return (
        <SignalCellular3BarRounded {...props} sx={{ color: colors.good }} />
      );
    case 2:
      return (
        <SignalCellular2BarRounded {...props} sx={{ color: colors.warn }} />
      );
    case 1:
      return (
        <SignalCellular1BarRounded {...props} sx={{ color: colors.warn }} />
      );
    default:
      return (
        <SignalCellular0BarRounded {...props} sx={{ color: colors.bad }} />
      );
  }
}

function typeLabel(type) {
  switch (type) {
    case 'wifi':
      return 'WiFi';
    case 'mobile':
      return 'Mobile';
    case 'dongle':
      return 'Dongle';
    case 'ethernet':
      return 'Ethernet';
    default:
      return 'Unknown';
  }
}

function formatIso(iso) {
  if (!iso) return '—';
  try {
    return DateTime.fromISO(iso, { zone: 'Europe/Copenhagen' })
      .setLocale('da')
      .toFormat('dd MMM yyyy HH:mm:ss');
  } catch {
    return iso;
  }
}

function InfoRow({ label, value }) {
  if (value == null) return null;
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="caption" fontFamily="monospace">
        {value}
      </Typography>
    </Stack>
  );
}

function buildDetailRows({ latestType, offline, connectionStatus }) {
  if (!latestType || !connectionStatus) return [];

  if (latestType === 'wifi') {
    const ts = formatIso(connectionStatus.LatestWifiTimestamp);
    return offline
      ? [
          { label: 'Last seen', value: ts },
          { label: 'SSID', value: connectionStatus.LatestWifiSsid ?? '—' },
        ]
      : [
          { label: 'Timestamp', value: ts },
          {
            label: 'RSSI',
            value:
              connectionStatus.LatestWifiSignalLevel != null
                ? `${connectionStatus.LatestWifiSignalLevel} dBm`
                : '—',
          },
          { label: 'SSID', value: connectionStatus.LatestWifiSsid ?? '—' },
          { label: 'IP', value: connectionStatus.LatestWifiIpAddress ?? '—' },
          { label: 'MAC', value: connectionStatus.LatestWifiMacAddress ?? '—' },
        ];
  }

  if (latestType === 'mobile') {
    const ts = formatIso(connectionStatus.LatestMobileTimestamp);
    return offline
      ? [
          { label: 'Last seen', value: ts },
          { label: 'APN', value: connectionStatus.LatestMobileApn || '—' },
        ]
      : [
          { label: 'Timestamp', value: ts },
          {
            label: 'RSSI',
            value:
              connectionStatus.LatestMobileRssi != null
                ? `${connectionStatus.LatestMobileRssi} dBm`
                : '—',
          },
          {
            label: 'RSRP',
            value:
              connectionStatus.LatestMobileRsrp != null
                ? `${connectionStatus.LatestMobileRsrp} dBm`
                : '—',
          },
          {
            label: 'RSRQ',
            value:
              connectionStatus.LatestMobileRsrq != null
                ? `${connectionStatus.LatestMobileRsrq} dB`
                : '—',
          },
          {
            label: 'CINR',
            value:
              connectionStatus.LatestMobileCinr != null
                ? `${connectionStatus.LatestMobileCinr} dB`
                : '—',
          },
          { label: 'IMSI', value: connectionStatus.LatestMobileImsi ?? '—' },
          { label: 'APN', value: connectionStatus.LatestMobileApn || '—' },
        ];
  }

  if (latestType === 'dongle') {
    const ts = formatIso(connectionStatus.LatestDongleTimestamp);
    return offline
      ? [{ label: 'Last seen', value: ts }]
      : [
          { label: 'Timestamp', value: ts },
          {
            label: 'Signal',
            value: connectionStatus.LatestDongleSignalLevel ?? '—',
          },
          { label: 'IMEI', value: connectionStatus.LatestDongleImei ?? '—' },
          {
            label: 'Workmode',
            value: connectionStatus.LatestDongleWorkmode ?? '—',
          },
          {
            label: 'Device',
            value: connectionStatus.LatestDongleDeviceName ?? '—',
          },
        ];
  }

  if (latestType === 'ethernet') {
    const ts = formatIso(connectionStatus.LatestEthernetTimestamp);
    return offline
      ? [{ label: 'Last seen', value: ts }]
      : [
          { label: 'Timestamp', value: ts },
          {
            label: 'IP',
            value: connectionStatus.LatestEthernetIpAddress ?? '—',
          },
          { label: 'MAC', value: connectionStatus.LatestEthernetMac ?? '—' },
        ];
  }

  return [];
}

function ConnectionIcon({
  latestType,
  connectionStatus,
  offline,
  colors,
  fontSize = 'small',
}) {
  if (latestType === 'wifi') {
    return (
      <WifiIcon
        rssi={connectionStatus?.LatestWifiSignalLevel}
        offline={offline}
        colors={colors}
        fontSize={fontSize}
      />
    );
  }
  if (latestType === 'mobile') {
    return (
      <MobileIcon
        rssi={connectionStatus?.LatestMobileRssi}
        offline={offline}
        colors={colors}
        fontSize={fontSize}
      />
    );
  }
  if (latestType === 'dongle') {
    return (
      <UsbRounded
        fontSize={fontSize}
        sx={{ color: offline ? colors.offline : colors.good }}
      />
    );
  }
  if (latestType === 'ethernet') {
    return (
      <SettingsEthernetRounded
        fontSize={fontSize}
        sx={{ color: offline ? colors.offline : colors.good }}
      />
    );
  }
  return null;
}

// ─── Panel (reusable — for detail pages, always fetches) ─────────────────────
export function GatewayStatusPanel({ gateway }) {
  const { t } = useTranslation('units-gateways-sensors');
  const theme = useTheme();

  const COLORS = {
    good: theme.palette.status.online,
    warn: theme.palette.status.degraded,
    bad: theme.palette.status.offline,
    offline: theme.palette.text.disabled,
  };

  const connectionStatus = gateway?.ConnectionStatus;
  const online = Boolean(gateway?.Online);
  const offline = !online;
  const latestType = connectionStatus
    ? getLatestConnectionType(connectionStatus)
    : null;

  const timestamp = React.useMemo(
    () => DateTime.utc().minus({ days: 3 }).toISO(),
    [],
  );
  const {
    data: connectionInfo,
    isFetching,
    isError,
  } = useGatewaysGetConnectionInfoAsyncByidqueryOptionsQuery(
    { id: gateway?.GatewayId, timestamp: [`ge(${timestamp})`] },
    { skip: !gateway?.GatewayId },
  );

  const statusLabel = offline
    ? t('gateways.statusOffline', { defaultValue: 'Offline' })
    : t('gateways.statusOnline', { defaultValue: 'Online' });
  const statusColor = offline ? COLORS.offline : COLORS.good;

  if (!latestType) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <SignalWifiOffRounded fontSize="small" sx={{ color: COLORS.bad }} />
        <Typography variant="body2" color="text.secondary">
          {t('common.notAvailable')}
        </Typography>
      </Box>
    );
  }

  const detailRows = buildDetailRows({ latestType, offline, connectionStatus });

  return (
    <Stack spacing={0}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: statusColor, fontWeight: 600 }}
        >
          {statusLabel}
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ height: 16, alignSelf: 'center' }}
        />
        <ConnectionIcon
          latestType={latestType}
          connectionStatus={connectionStatus}
          offline={offline}
          colors={COLORS}
        />
        <Typography variant="subtitle2" color="text.secondary">
          {typeLabel(latestType)}
        </Typography>
      </Stack>

      <Divider sx={{ mb: 1 }} />

      {/* Connection details */}
      {detailRows.length > 0 && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}
          >
            {t('gateways.connectionDetails', {
              defaultValue: 'Connection details',
            })}
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {detailRows.map((row, idx) => (
              <InfoRow key={idx} label={row.label} value={row.value} />
            ))}
          </Stack>
          <Divider sx={{ mb: 1 }} />
        </>
      )}

      {/* Sparkline */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5 }}
      >
        {t('gateways.signalHistory', {
          defaultValue: 'Signal history (3 days)',
        })}
      </Typography>
      {isFetching && (
        <Typography variant="body2" color="text.secondary">
          {t('gateways.loadingHistory')}
        </Typography>
      )}
      {isError && (
        <Typography variant="body2" color="error">
          {t('gateways.failedToLoadConnectionHistory')}
        </Typography>
      )}
      {!isFetching && !isError && (
        <GatewaySignalSparkline
          dataPoints={connectionInfo}
          online={online}
          connectionType={latestType}
          height={56}
        />
      )}
    </Stack>
  );
}

// ─── Main cell ────────────────────────────────────────────────────────────────
export default function GatewayStatusCell({ gateway }) {
  const { t } = useTranslation('units-gateways-sensors');
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const COLORS = {
    good: theme.palette.status.online,
    warn: theme.palette.status.degraded,
    bad: theme.palette.status.offline,
    offline: theme.palette.status.offline,
  };

  const connectionStatus = gateway?.ConnectionStatus;
  const online = Boolean(gateway?.Online);
  const offline = !online;
  const latestType = connectionStatus
    ? getLatestConnectionType(connectionStatus)
    : null;

  const statusLabel = offline
    ? t('gateways.statusOffline', { defaultValue: 'Offline' })
    : t('gateways.statusOnline', { defaultValue: 'Online' });
  const statusColor = offline ? COLORS.offline : COLORS.good;

  const timestamp = React.useMemo(
    () => DateTime.utc().minus({ days: 3 }).toISO(),
    [],
  );
  const {
    data: connectionInfo,
    isFetching,
    isError,
  } = useGatewaysGetConnectionInfoAsyncByidqueryOptionsQuery(
    { id: gateway?.GatewayId, timestamp: [`ge(${timestamp})`] },
    { skip: !open || !gateway?.GatewayId },
  );

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = (e) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const detailRows = buildDetailRows({ latestType, offline, connectionStatus });

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          maxWidth: 75,
          minWidth: 75,
        }}
      >
        <StyledBadgeWrapper color={statusColor} enableRipple={false} />
        <Typography variant="body2">{statusLabel}</Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 1.5, minWidth: 280, maxWidth: 380 },
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" sx={{ color: statusColor }}>
            {statusLabel}
          </Typography>

          {latestType && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              {/* <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 16, alignSelf: 'center' }}
              /> */}
              <ConnectionIcon
                latestType={latestType}
                connectionStatus={connectionStatus}
                offline={offline}
                colors={COLORS}
              />
              <Typography variant="subtitle2" color="text.secondary">
                {typeLabel(latestType)}
              </Typography>
            </Box>
          )}
        </Stack>

        <Divider sx={{ mb: 1 }} />

        {/* Connection details */}
        {detailRows.length > 0 && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              {t('gateways.connectionDetails', {
                defaultValue: 'Connection details',
              })}
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              {detailRows.map((row, idx) => (
                <InfoRow key={idx} label={row.label} value={row.value} />
              ))}
            </Stack>
            <Divider sx={{ mb: 1 }} />
          </>
        )}

        {/* Sparkline */}
        {latestType && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              {t('gateways.signalHistory', {
                defaultValue: 'Signal history (3 days)',
              })}
            </Typography>
            {isFetching && (
              <Typography variant="body2" color="text.secondary">
                {t('gateways.loadingHistory')}
              </Typography>
            )}
            {isError && (
              <Typography variant="body2" color="error">
                {t('gateways.failedToLoadConnectionHistory')}
              </Typography>
            )}
            {!isFetching && !isError && (
              <GatewaySignalSparkline
                dataPoints={connectionInfo}
                online={online}
                connectionType={latestType}
                height={56}
              />
            )}
          </>
        )}
      </Popover>
    </>
  );
}
